<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Credentials</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search" 
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UInputMenu
                    v-model="filters.partner"
                    :search="searchPartner"
                    class="w-52"
                    placeholder="Partner"
                    option-attribute="name"
                    leading-icon="i-ph-handshake-duotone"
                    trailing
                    nullable
                    by="id"
                />
                <USelectMenu
                    v-model="filters.status"
                    trailing
                    :class="filters.status ? 'w-44' : 'w-40'"
                    :leading-icon="filters.status ? undefined : 'i-ph-list-numbers-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="[
                        { name: 'Used', value: 'used', dot: 'bg-orange-500' },
                        { name: 'Valid', value: 'valid', dot: 'bg-green-500' },
                        { name: 'Expired', value: 'expired', dot: 'bg-red-500' },
                    ]"
                >
                    <template #label>
                        <template v-if="filters.status">
                            <span class="text-gray-400">Status:</span>
                            <span class="truncate font-medium">{{ filters.status.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Status</span>
                    </template>
                    <template #option="{ option }">
                        <span :class="[option.dot, 'inline-block size-1.5 mx-1 flex-shrink-0 rounded-full']" aria-hidden="true" />
                        <span class="truncate">{{ option.name }}</span>
                    </template>
                    <template #trailing v-if="filters.status">
                        <button @click.stop="filters.status = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
                <UButton
                    label="Clear"
                    color="gray"
                    @click="clearFilters()"
                    class="text-center"
                />
                <UButton
                    label="Import"
                    @click="overlays.import = true"
                    color="black"
                    icon="i-ph-download-bold"
                />
                <UButtonGroup size="sm" orientation="horizontal">
                    <UButton
                        label="Bulk Activation"
                        @click="overlays.bulkActivate = true"
                        icon="i-ph-shield-plus-bold"
                    />
                    <UDropdown
                        :popper="{ placement: 'bottom-end' }"
                        :items="[[
                            {
                                label: 'History',
                                icon: 'i-ph-clock-counter-clockwise-duotone',
                                click: () => navigateTo('/bulk-activations'),
                            },
                        ]]"
                    >
                        <UButton
                            icon="i-heroicons-chevron-down-20-solid"
                            class="border-l border-white/30"
                        />
                    </UDropdown>
                </UButtonGroup>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="credentials"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-three-duotone', label: 'No credentials.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #id-data="{ row }">
                    <p class="font-mono">
                        {{ row.number.toString().padStart(8, '0') }}
                    </p>
                </template>
                <template #partner-data="{ row }">
                    <div class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.partner.name" :path="row.partner.avatar" />
                        <span>{{ row.partner.name }}</span>
                    </div>
                </template>
                <template #customer-data="{ row }">
                    <ULink v-if="row.customer" :to="`/customers/${row.customer.id}`" class="hover:underline">
                        {{ [row.customer.first_name, row.customer.middle_name, row.customer.last_name].filter(Boolean).join(' ') }}
                    </ULink>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #fields-data="{ row }">
                    <div class="flex flex-wrap items-center gap-1">
                        <template v-for="field in Object.keys(row.fields)" :key="field">
                            <UBadge v-if="field === 'exclusion'" color="gray" variant="solid">
                                <span class="flex items-center gap-1">
                                    <span>exclusion:</span>
                                    <TruncatedText :value="row.fields[field]" />
                                </span>
                            </UBadge>
                            <UBadge
                                v-else
                                :label="`${field}: ${row.fields[field]}`"
                                color="gray"
                                variant="solid"
                            />
                        </template>
                    </div>
                </template>
                <template #expiry-data="{ row }">
                    <span>{{ dayjs(row.expires_at).format('YYYY-MM-DD') }}</span>
                </template>
                <template #status-data="{ row }">
                    <UBadge v-if="row.invalidated_at" color="orange" label="Used" variant="subtle" />
                    <UBadge v-else-if="dayjs(row.expires_at).isBefore(dayjs())" color="red" label="Expired" variant="subtle" />
                    <UBadge v-else color="green" label="Valid" variant="subtle" />
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayCredentialImportCredentials v-model:open="overlays.import" @reload="fetchData" />
        <OverlayCredentialBulkActivation v-model:open="overlays.bulkActivate" @reload="fetchData" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { UserRole } from '~/types/user'
import { watchDebounced } from '@vueuse/core'
import type { Credential } from '~/types/credential'
import { SubscriptionSourceType } from '~/types/subscription'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Credentials' })

const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const search = ref('')
const filters = ref<{ [key: string]: any }>({})
const credentials = ref<Credential[]>([])

const overlays = reactive({
    import: false,
    bulkActivate: false,
})

const columns = [
    { key: 'id', label: 'ID' },
    { key: 'partner', label: 'Partner' },
    { key: 'customer', label: 'Customer' },
    { key: 'fields', label: 'Fields' },
    { key: 'expiry', label: 'Expiry Date' },
    { key: 'status', label: 'Status' },
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })
watch(filters, () => fetchData(), { deep: true })
watch(
    () => pagination.from,
    () => fetchData(),
)

async function fetchData() {
    loading.value = true
    credentials.value = []
    pagination.setTotal(undefined)

    let query = client
        .from('credentials')
        .select(`
            id, number, fields, invalidated_at, expires_at, updated_at,
            partner:partners (id, name, avatar)
        `,
            { count: 'exact' },
        )
        .order('number', { ascending: false })

    if (search.value) {
        const numberQuery = parseInt(search.value.replace(/[^\d]/g, ''))
        const escapedSearch = escapeLike(search.value)

        query = query.or(
            (!isNaN(numberQuery) && numberQuery <= 2147483647 ? `number.eq.${numberQuery},` : '') +
            `fields->>policy_holder.ilike.%${escapedSearch}%,` +
            `fields->>beneficiary_pin.ilike.${escapedSearch},` +
            `fields->>id_number.ilike.${escapedSearch},` +
            `fields->>individual_number.ilike.${escapedSearch},` +
            `fields->>insured_number.ilike.${escapedSearch}`
        )
    }

    if (filters.value.partner) {
        query = query.eq('partner_id', filters.value.partner.id)
    }

    if (filters.value.status) {
        if (filters.value.status.value === 'valid') query = query.is('invalidated_at', null).gt('expires_at', new Date().toISOString())
        else if (filters.value.status.value === 'used') query = query.not('invalidated_at', 'is', null)
        else if (filters.value.status.value === 'expired') query = query.is('invalidated_at', null).lte('expires_at', new Date().toISOString())
    }

    const { count, data } = await query.range(pagination.from, pagination.to)

    if (count && data) {
        credentials.value = data
        pagination.setTotal(count)
        await setCredentialCustomers()
    }

    loading.value = false
}

async function setCredentialCustomers() {
    const credentialIds = credentials.value.filter((c) => !!c.invalidated_at).map((c) => c.id)

    const { data: subscriptions } = await client.from('subscriptions')
        .select('id, customer_id, source_id, customer:customers (id, first_name, middle_name, last_name)')
        .in('source_id', credentialIds)
        .eq('source_type', SubscriptionSourceType.credential)

    if (subscriptions?.length) {
        credentials.value = credentials.value.map((credential) => ({
            ...credential,
            customer: subscriptions.find((s) => s.source_id === credential.id)?.customer
        }))
    }
}

async function searchPartner(q: string) {
    const { data, error } = await client.from('partners').select('id, name').is('deleted_at', null).ilike('name', `%${q}%`)

    if (error) {
        return []
    }

    return data
}

function clearFilters() {
    search.value = ''
    filters.value = {}
}
</script>
